import { RatingsService } from '@/services';
import { QueryCoralcommentsInternalArgs } from '@/services/comments/service';
import {
  AddRatingInternalInput,
  QueryCoralcommentsArgs,
  RatingAverageResponse,
  RatingResponse,
} from '@repo/api/open-calls/graphql';
import { createInitialState, InitialState } from '@repo/ui/state';
import { create } from 'zustand';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { toast } from 'sonner';

const initialState = createInitialState<RatingResponse[]>([]);

export interface RatingsState extends InitialState<RatingResponse[]> {
  ratingAverage: RatingAverageResponse | null;
  createRating: (variables: { input: AddRatingInternalInput }) => Promise<void>;
  fetchRatings: (variables: QueryCoralcommentsInternalArgs) => Promise<void>;
  fetchCandidateRatingAverage: (variables: {
    submissionID: string;
  }) => Promise<void>;
  reset: () => void;
}

export const store = create<RatingsState>((set, get) => ({
  data: initialState.data,
  error: initialState.error,
  loading: initialState.loading,
  ratingAverage: null,
  createRating: async (variables: { input: AddRatingInternalInput }) => {
    set((state) => ({ ...state, error: '', loading: true }));
    const { average, count } = get().ratingAverage || {
      average: 0,
      count: 0,
    };
    try {
      await RatingsService.createRating(variables);
      set((state) => ({
        ...state,
        ratingAverage: {
          ...state.ratingAverage,
          average: (average * count + variables.input.rating) / (count + 1),
          count: count + 1,
        } as RatingAverageResponse,
      }));
      toast.message('Rating added successfully');
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to add rating' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchRatings: async (variables: QueryCoralcommentsArgs) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const data = await RatingsService.fetchRatings(variables);
      set((state) => ({ ...state, data: data ?? [] }));
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to fetch ratings' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchCandidateRatingAverage: async (variables: { submissionID: string }) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const ratingAverage = await RatingsService.fetchCandidateRatingAverage(
        variables.submissionID,
      );
      set((state) => ({
        ...state,
        ratingAverage,
      }));
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to fetch rating average' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  reset: () => {
    set((state) => ({
      ...state,
      data: initialState.data,
      error: initialState.error,
      loading: initialState.loading,
      ratingAverage: null,
    }));
  },
}));
